import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { BehaviorSubject, Subscription, filter, merge } from 'rxjs';

import { AbstractBaseStyleFieldComponent } from '../../BaseStyleField/abstract-base-style-field.component';
import { AbstractPageBuilderService } from '../../../services/abstract-page-builder.service';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { DropdownFieldModel } from '../../../models/dropdown-field.model';
import { DropdownOptionModel } from '../../../../Forms/models/dropdown-option.model';
import { LayoutBlockDisplayRatiosEnum, layoutBlockDisplayRatiosToWidthPercentMap } from '../../../enums/layout-block-display-ratios.enum';
import { LayoutStyleField, columnNumbersToDisplayRatiosMap } from '../../../models/layout-style-field.model';
import { LayoutStyle } from '../../../models/layout-style.model';
import { ManageStylesDialogComponent } from '../../../../Styles/components/ManageStylesDialog/manage-styles-dialog.component';
import { PaddingModel } from '../../../models/padding.model';
import { RadioTypesEnum } from '../../../../Forms/enums/radio-types.enum';
import { SaveStylesDialogComponent } from '../../../../Dialogs/components/SaveStylesDialog/save-styles-dialog.component';
import { SaveTwigTemplateDialogComponent } from '../../../../Dialogs/components/SaveTwigTemplateDialog/save-twig-template-dialog.component';
import { Style } from '../../../../Styles/models/style.model';
import { StylesService } from '../../../../Styles/services/styles.service';
import { StyleStateEnum } from '../../../enums/style-state.enum';
import { Template } from '../../../../Styles/models/template.model';
import { VerticalAlignmentEnum } from '../../../../Forms/enums/vertical-alignment.enum';

import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';
import { BlockDisplayTypeEnum } from '../../../enums/block-display-type.enum';
import { CornerRadiusModel } from '@Common/PageBuilder/models/corner-radius.model';
import { AdvancedColorPickerFieldModel, backgroundColorConfig } from '@Common/PageBuilder/models/advanced-color-picker-field.model';


@Component({
    selector: 'agpr-layout-style-field',
    templateUrl: './layout-style-field.component.html',
    styleUrls: ['./layout-style-field.component.scss'],
    standalone: false
})
export class LayoutStyleFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnChanges, OnDestroy {

    @Input() declare field: LayoutStyleField;
    @Input() declare value: LayoutStyle;
    @Input() isCanvas: boolean = false; // only used for customizations tab
    @Input() textPrimaryLabel: boolean = false;

    @Output() changes: EventEmitter<LayoutStyle> = new EventEmitter<LayoutStyle>();

    layoutStyleFormGroup: FormGroup;
    columnNumber: number;
    styleStateField: DropdownFieldModel;
    displayRatioDropdownOptions: DropdownOptionModel[] = [];
    selectedRatio: DropdownOptionModel;
    styles: Style[];
    colorPickerField: AdvancedColorPickerFieldModel;
    initted: boolean = false;
    showCornerRadius: boolean = false;
    defaultStyleStateOptions: DropdownOptionModel[] = [
        { label: 'PageBuilder.formField.styles.defaultAgilityStyle', value: StyleStateEnum.DEFAULT },
        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
    ];

    subscriptions: Subscription[] = [];

    readonly verticalAlignments = VerticalAlignmentEnum;
    readonly radioTypes = RadioTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly displayRatioEnumToArrayMap: Map<LayoutBlockDisplayRatiosEnum, number[]> = new Map([
        [LayoutBlockDisplayRatiosEnum.ONE_TO_ONE, [1, 1]],
        [LayoutBlockDisplayRatiosEnum.THREE_TO_SEVEN, [3, 7]],
        [LayoutBlockDisplayRatiosEnum.SEVEN_TO_THREE, [7, 3]],
        [LayoutBlockDisplayRatiosEnum.TWO_TO_EIGHT, [1, 4]],
        [LayoutBlockDisplayRatiosEnum.EIGHT_TO_TWO, [4, 1]],
        [LayoutBlockDisplayRatiosEnum.ONE_TO_ONE_TO_ONE, [1, 1, 1]],
        [LayoutBlockDisplayRatiosEnum.TWO_TO_SIX_TO_TWO, [1, 3, 1]],
        [LayoutBlockDisplayRatiosEnum.TWO_TO_TWO_TO_SIX, [1, 1, 3]],
        [LayoutBlockDisplayRatiosEnum.SIX_TO_TWO_TO_TWO, [3, 1, 1]]
    ]);

    constructor(
        public coreDialogService: CoreDialogService,
        public stylesService: StylesService,
        public abstractPageBuilderService: AbstractPageBuilderService
    ) {
        super(coreDialogService);
    }

    ngOnInit(): void {
        if ((this.block?.displayElement?.displayType === BlockDisplayTypeEnum.ONLINE) || this.isCanvas) {
            this.showCornerRadius = true;
        }

        this.columnNumber = this.field.columnNumber;

        if (this.block?.displayElement) {
            this.field.allowExpertMode = this.block?.displayElement?.displayType !== BlockDisplayTypeEnum.ONLINE;
        }

        this.displayRatioDropdownOptions = columnNumbersToDisplayRatiosMap.get(this.columnNumber)
            .map(ratio => new DropdownOptionModel(ratio, ratio));
        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        this.styleStateField = new DropdownFieldModel(
            null,
            'displayStyleState',
            'PageBuilder.styles.contentBlockStyle',
            '',
            this.defaultStyleStateOptions
        );


        this.colorPickerField = AdvancedColorPickerFieldModel.create({
            ...backgroundColorConfig(true),
            initialValue: this.value.backgroundColor || '#FFFFFF'
        });


        this.setUpFormGroup();
        this.initted = true;

        this.subscriptions.push(
            this.stylesService.all$.pipe(
                filter(x => !!x)
            ).subscribe(styles => {
                this.styles = styles;
                this.updateStyleOptions();
                this.updateStyleFromSavedStyle();
            }),
            this.styleStateFormControl.valueChanges.subscribe(styleState => this.setStyle(styleState, this.styleStateField.property)),
            this.displayRatioFormControl.valueChanges.subscribe(displayRatio => {
                this.setStyle(displayRatio, 'displayRatio');
                this.setStyle(layoutBlockDisplayRatiosToWidthPercentMap.get(displayRatio), 'width');
                this.abstractPageBuilderService.updateChildBlockWidths(this.block as LayoutBlock);
            }),
            this.columnSpacingFormControl.valueChanges.subscribe(columnSpacing => this.setStyle(columnSpacing, 'columnSpacing')),
            this.backgroundColorFormControl.valueChanges.subscribe(backgroundColor => this.setStyle(backgroundColor, 'backgroundColor')),
            merge(
                this.verticalAlignmentFormGroup.get('top').valueChanges,
                this.verticalAlignmentFormGroup.get('middle').valueChanges,
                this.verticalAlignmentFormGroup.get('bottom').valueChanges
            ).subscribe(verticalAlignment => {
                verticalAlignment = verticalAlignment;
                this.setStyle(verticalAlignment, 'verticalAlignment');
                this.verticalAlignmentFormGroup.get('top').setValue(verticalAlignment, { emitEvent: false });
                this.verticalAlignmentFormGroup.get('middle').setValue(verticalAlignment, { emitEvent: false });
                this.verticalAlignmentFormGroup.get('bottom').setValue(verticalAlignment, { emitEvent: false });
            }),
            this.paddingFormControl.valueChanges.subscribe(padding => this.setStyle(padding, 'padding')),
            this.cornerRadiusFormControl.valueChanges.subscribe(cornerRadius => this.setStyle(cornerRadius, 'cornerRadius')),
            this.stylesFormGroup.valueChanges.subscribe(value => {
                this.styleStateFormControl.setValue(this.value['displayStyleState'], { emitEvent: false });
                this.changes.emit(this.value);
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value && this.initted) {
            this.updateFormGroup();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setUpFormGroup(): void {
        this.layoutStyleFormGroup = new FormGroup({
            styleState: new FormControl<number | string>(this.value['displayStyleState']),
            styles: new FormGroup({
                displayRatio: new FormControl<LayoutBlockDisplayRatiosEnum>(this.value['displayRatio'] || this.displayRatioDropdownOptions[0]?.value || null),
                columnSpacing: new FormControl<number>(this.value['columnSpacing']),
                backgroundColor: new FormControl<string>(this.value['backgroundColor']),
                verticalAlignment: new FormGroup({
                    top: new FormControl<VerticalAlignmentEnum>(this.value['verticalAlignment'] || VerticalAlignmentEnum.TOP),
                    middle: new FormControl<VerticalAlignmentEnum>(this.value['verticalAlignment'] || VerticalAlignmentEnum.TOP),
                    bottom: new FormControl<VerticalAlignmentEnum>(this.value['verticalAlignment'] || VerticalAlignmentEnum.TOP),
                }),
                padding: new FormControl<PaddingModel>(this.value['padding'] || new PaddingModel()),
                cornerRadius: new FormControl<CornerRadiusModel>(this.value['cornerRadius'] || new CornerRadiusModel())
            })
        });
    }

    /**
     * Opens the save-twig-template-dialog after loading the data needed for it
     */
    public saveExpertTemplate(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.template = this.value.template;
        this.saveTwigTemplateDialogModel.inputs.defaultTemplate = this.abstractPageBuilderService.getDefaultTemplateByBlockType(
            this.abstractPageBuilderService.getBlockTypeEnumFromStyleTypeEnum(this.field.styleType),
            StyleStateEnum.DEFAULT
        );
        // Set the default template if there's not a template on this style already
        if (!this.styleModel.template) {
            this.setTemplate = true;
            this.styleModel.template = new Template();
            this.styleModel.template.template = this.saveTwigTemplateDialogModel.inputs.defaultTemplate;
        }

        this.saveTwigTemplateDialogModel.inputs.type = this.field.styleType;
        this.saveTwigTemplateDialogModel.inputs.inputStyle = this.styleModel;
        const dialogKey = this.coreDialogService.open(SaveTwigTemplateDialogComponent, this.saveTwigTemplateDialogModel);
    }

    public saveStyle(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;

        const io: DialogIoModel = {
            inputs: {
                type: this.field.styleType,
                inputStyle: this.styleModel
            },
            outputs: {
                dialogClose: (response) => {
                    if (response) {
                        this.setStyle(response, 'displayStyleState', true);
                    }
                }
            }
        };

        this.coreDialogService.open(SaveStylesDialogComponent, io);
    }

    manageStylesButtonClicked(){
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageStylesDialogComponent, io);
    }

    protected updateStyleOptions(): void {
        if (!this.styles || !this.value || !this.value.styleState) {
            return;
        }
        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        const styleDropdownOptions = this.styles
            .filter(style => style.type === this.field.styleType)
            .map(
                style => {
                    return {
                        label: style.name,
                        value: style.id
                    };
                }
            );

        this.styleStateField.options = [
            ...this.defaultStyleStateOptions,
            ...styleDropdownOptions
        ];
    }

    protected updateStyleFromSavedStyle() {
        if (!this.value || !this.styles || !Number(this.value.styleState)) {
            return;
        }
        const savedStyle: Style = this.styles.find(x => x.id === this.value.styleState as number);
        if (!savedStyle) {
            // the list doesn't contain the id we're looking for, can't load it
            return;
        }
        const layoutStyleModel = new LayoutStyle(savedStyle.style);
        layoutStyleModel.styleState = this.value.styleState;
        layoutStyleModel.displayStyleState = this.value.displayStyleState;
        layoutStyleModel.template = savedStyle.template;

        this.value = layoutStyleModel;

        if (!this.value.displayRatio && this.columnNumber > 1) {
            this.value.displayRatio = this.displayRatioDropdownOptions[0].value;
        }
        this.updateFormGroup();
    }

    updateFormGroup(): void {
        this.styleStateFormControl.setValue(this.value['displayStyleState'], { emitEvent: false });
        this.displayRatioFormControl.setValue(this.value['displayRatio'] || this.displayRatioDropdownOptions[0]?.value || null, { emitEvent: false });
        this.columnSpacingFormControl.setValue(this.value['columnSpacing'], { emitEvent: false });
        this.backgroundColorFormControl.setValue(this.value['backgroundColor'], { emitEvent: false });
        this.verticalAlignmentFormGroup.get('top').setValue(this.value['verticalAlignment'] || VerticalAlignmentEnum.TOP, { emitEvent: false });
        this.verticalAlignmentFormGroup.get('middle').setValue(this.value['verticalAlignment'] || VerticalAlignmentEnum.TOP, { emitEvent: false });
        this.verticalAlignmentFormGroup.get('bottom').setValue(this.value['verticalAlignment'] || VerticalAlignmentEnum.TOP, { emitEvent: false });
        this.paddingFormControl.setValue(this.value['padding'] || new PaddingModel(), { emitEvent: false });
        this.cornerRadiusFormControl.setValue(this.value['cornerRadius'] || new PaddingModel(), { emitEvent: false });
    }

    protected displayStyleStateChanged(): void {
        this.styleStateFormControl.setValue(this.value.displayStyleState, { emitEvent: false });
    }

    get styleStateFormControl(): FormControl {
        return this.layoutStyleFormGroup.get('styleState') as FormControl;
    }

    get stylesFormGroup(): FormGroup {
        return this.layoutStyleFormGroup.get('styles') as FormGroup;
    }

    get displayRatioFormControl(): FormControl {
        return this.stylesFormGroup.get('displayRatio') as FormControl;
    }

    get columnSpacingFormControl(): FormControl {
        return this.stylesFormGroup.get('columnSpacing') as FormControl;
    }

    get backgroundColorFormControl(): FormControl {
        return this.stylesFormGroup.get('backgroundColor') as FormControl;
    }

    get verticalAlignmentFormGroup(): FormGroup {
        return this.stylesFormGroup.get('verticalAlignment') as FormGroup;
    }

    get paddingFormControl(): FormControl {
        return this.stylesFormGroup.get('padding') as FormControl;
    }

    get cornerRadiusFormControl(): FormControl {
        return this.stylesFormGroup.get('cornerRadius') as FormControl;
    }
}
